import React, { useState, useEffect, Fragment } from 'react';
import {
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { WideBox } from '../../styles/styles';
import { useRootStore } from '../../../stores/BridgeProvider';
import { ContactAgentSchema } from '../../../validation';
import { NumberHandler } from '../../util/NumberHandler';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import DefaultComm from './DefaultComm';
import Spinner from '../../util/Spinner';

const ContactAgents = () => {
  const { authStore } = useRootStore();
  const [agentList, setAgentList] = useState([]);
  const [orgInfo, setOrgInfo] = useState([]);
  const [isEditing, setEditing] = useState(false);
  const [isLoading, setLoading] = useState(true);

  const getOrgInfo = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/organization-info`,
      );
      setOrgInfo(data);
    } catch (e) {
      console.log(e);
    }
  };

  const getContactAgentData = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/contact-agents`,
      );
      const agents = data.map(agent => {
        let { RentalSplit, SaleSplit } = agent;
        RentalSplit *= 100;
        SaleSplit *= 100;
        return { ...agent, RentalSplit, SaleSplit };
      });
      setAgentList(agents);
    } catch (e) {
      console.log(e);
    }
  };
  const validateValue = async ({ valueName, value }) => {
    const result = await yup
      .reach(ContactAgentSchema, valueName)
      .isValid(value);
    return {
      isValid: result,
      error: result ? null : `Error validating ${valueName}`,
    };
  };
  const handleChange = async (cellInfo, value, valueName) => {
    const agentListClone = [...agentList];
    const contactAgentClone = agentListClone[cellInfo.index];
    contactAgentClone[valueName] = value;
    contactAgentClone.dirty = true;

    const { isValid, error } = await validateValue({ valueName, value });

    // TODO:
    if (!isValid) {
      contactAgentClone[`${valueName}_error`] = error;
    } else if (contactAgentClone[`${valueName}_error`]) {
      contactAgentClone[`${valueName}_error`] = null;
    }
    setAgentList(agentListClone);
  };

  const wipeChanges = () => {
    getContactAgentData();
    setEditing(false);
  };

  const saveAgentContact = async (agentToUpdate, i) => {
    try {
      const { status } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/contact-agent`,
        {
          ContactId: agentToUpdate.ContactId,
          RentalSplit: agentToUpdate.RentalSplit / 100,
          SaleSplit: agentToUpdate.SaleSplit / 100,
        },
      );
      if (status === 200) {
        const agentListClone = [...agentList];
        const contactAgentClone = agentListClone[i];
        contactAgentClone.dirty = false;
        setAgentList(agentListClone);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const saveAgentList = async () => {
    let errors = false;
    const requests = [];

    await Promise.all(
      agentList.map(async (ac, i) => {
        if (ac.dirty) {
          try {
            const valid = await ContactAgentSchema.isValid(ac);
            // FIXME: if not valid DO SOMETHING
            if (!valid) {
              errors = true;
              return;
            }
            requests.push(saveAgentContact(ac, i));
          } catch (e) {
            console.error(e);
          }
        }
      }),
    );

    await Promise.all(requests);
    if (!errors) {
      wipeChanges();
    }
  };

  const EditableNumberCell = props => {
    const { cellInfo, valueName, ...rest } = props;
    const contactAgent = agentList[cellInfo.index];
    const parseNumberVal = val => {
      return parseFloat(val).toFixed(2);
    };
    const cellVal = parseNumberVal(contactAgent[valueName]);
    const [curVal, setCurVal] = useState(cellVal);
    return (
      <TextField
        key={cellInfo}
        name={valueName}
        label={valueName}
        type="number"
        value={curVal}
        margin="normal"
        onChange={e => setCurVal(e.target.value)}
        onBlur={() =>
          curVal !== cellVal ? handleChange(cellInfo, curVal, valueName) : null
        }
        variant="outlined"
        fullWidth
        InputProps={{
          endAdornment: <InputAdornment position="start">%</InputAdornment>,
        }}
        // error={showError('Amount')}
        {...rest}
      />
    );
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'Name',
    },
    {
      Header: 'Rental Commission',
      accessor: ca => ca.RentalSplit || 'N/A',
      Cell: isEditing
        ? ({ row }) => (
            <EditableNumberCell cellInfo={row} valueName="RentalSplit" />
          )
        : ({ row }) => (
            <NumberHandler
              num={row.original.RentalSplit}
              float={2}
              justify="center"
              suffix="%"
            />
          ),
      id: 'rentalCommission',
    },
    {
      Header: 'Sales Commission',
      accessor: ca => ca.SaleSplit || null,
      Cell: isEditing
        ? ({ row }) => (
            <EditableNumberCell cellInfo={row} valueName="SaleSplit" />
          )
        : ({ row }) => (
            <NumberHandler
              num={row.original.SaleSplit}
              float={2}
              justify="center"
              suffix="%"
            />
          ),
      id: 'salesCommission',
    },
    {
      Header: '',
      width: 40,
      Cell: ({ row }) => <ContactDropdown row={row} />,
      id: 'contactAgentLink',
      show: !isEditing,
    },
  ];

  useEffect(() => {
    const initialize = async () => {
      const requests = [getContactAgentData(), getOrgInfo()];
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  if (isLoading) return <Spinner />;

  const ContactDropdown = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const open = Boolean(anchorEl);

    const handleClick = event => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Fragment>
        <IconButton
          onClick={e => handleClick(e)}
          style={{ padding: 0, height: 'fit-content' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem component={Link} to={`/contacts/${row.original.ContactId}`}>
            Edit Contact Info
          </MenuItem>
        </Menu>
      </Fragment>
    );
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <DefaultComm orgInfo={orgInfo} getOrgInfo={getOrgInfo} />
      <WideBox>
        <CustomTable
          data={agentList}
          columns={columns}
          minRows={1}
          tableHeader={<Typography variant="h4">Contact Agents</Typography>}
          actionables={
            !isEditing ? (
              <div>
                <Button
                  color="primary"
                  variant="outlined"
                  style={{ marginRight: '10px' }}
                  component={Link}
                  to={{
                    pathname: '/contacts/new',
                    state: { fromContactAgent: true },
                  }}
                >
                  + Agent
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setEditing(true)}
                >
                  Edit Details
                </Button>
              </div>
            ) : (
              <Button
                color="secondary"
                variant="outlined"
                onClick={wipeChanges}
              >
                Cancel
              </Button>
            )
          }
        />
        {isEditing && (
          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={saveAgentList}
              disabled={agentList.every(agent => agent.dirty !== true)}
            >
              Save Changes
            </Button>
          </div>
        )}
      </WideBox>
    </div>
  );
};

export default ContactAgents;
